import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useToggle } from 'hooks';
import Modal from 'components/shared/modal';
import ClampedText from 'components/shared/Text';
import { Icon, ButtonGroup, Button } from 'ui';
import { ItemTypes } from 'constants/itemTypes';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import Skeleton from 'ui/Skeleton';
import RoundedInput from './RoundedInput';
import Response from './Response';

const Interaction = ({
  interaction: initialInteraction,
  updateInteraction,
  deleteInteraction,
  showDelete,
  scenarioId,
  moveInteraction,
  shouldCollapse,
  setShouldCollapse,
  expanded: initialExpanded,
  addResponseApi,
  deleteResponseApi,
  updateResponseApi,
  hideBorder,
  showStrongErrors,
  index,
}) => {
  const ref = useRef(null);
  const { position, id } = initialInteraction;
  const [expanded, toggleExpanded] = useToggle(initialExpanded);
  const [subject, setSubject] = useState(initialInteraction.subject);
  const [responses, setResponses] = useState(initialInteraction.responses);
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [hover, setHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const orderedResponses =
    responses[0].position !== undefined
      ? responses.sort((a, b) => a.position - b.position)
      : responses.sort((a, b) => a.id - b.id);

  const [{ handlerId, dropped }, drop] = useDrop({
    accept: ItemTypes.INTERACTION,
    collect(monitor) {
      return {
        dropped: monitor.didDrop(),
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveInteraction(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.INTERACTION,
    item: () => {
      return { id, index, position, subject };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isDragging && dropped) {
      setShouldUpdate(true);
    }
  }, [isDragging, dropped, shouldUpdate]);

  drag(drop(ref));

  useEffect(() => {
    if (shouldUpdate) {
      updateInteraction({ position, subject, responses, id });
      setShouldUpdate(false);
    }
  }, [id, position, responses, shouldUpdate, subject, updateInteraction]);
  useEffect(() => {
    if (!initialExpanded && expanded) toggleExpanded();
    if (shouldCollapse && hover) {
      setHover(false);
    }
    //eslint-disable-next-line
  }, [initialExpanded, shouldCollapse]);

  const addResponse = () => {
    setIsLoading(true);
    const newResponse = { subject: '', explanation: '', correct: false };
    addResponseApi(newResponse).then(({ response }) => {
      setResponses(res => [...res, response]);
      setShouldUpdate(true);
      setIsLoading(false);
    });
  };

  const deleteResponse = responseId => {
    deleteResponseApi(responseId).then(() => {
      setResponses(res => res.filter(r => r.id !== responseId));
      setShouldUpdate(true);
    });
  };

  const dropdownStyles = classNames(
    'h-56 w-fill bdrs-2 d-f jc-sb ai-c border-solid border-1 bg-teal-lightest border-teal-lightest f-o-n mt-8 ta-l OnDemandPracticeConversation-interactionHeader pr-32 pl-32',
    {
      'OnDemandPracticeConversation-interactionHeader--isHovered':
        !(isDragging || shouldCollapse) && hover,
      'OnDemandPracticeConversation-interactionHeader--isDragging': isDragging,
    },
  );
  const renderHeader = () => (
    <div
      role="button"
      onClick={() => toggleExpanded()}
      onKeyDown={e => e.key === 'Enter' && toggleExpanded()}
      tabIndex={0}
      className={dropdownStyles}
      onDragStart={() => setShouldCollapse(true)}
      onDragEnd={() => setShouldCollapse(false)}
      onMouseEnter={() => {
        if (!isDragging && !shouldCollapse) setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
    >
      {!expanded && (
        <div className="f-o-n u-resetBtn OnDemandPracticeConversation-moveInteraction Animated Animated--fadeIn">
          <Icon name="bars" className="fz-20 c-gray-darker h-c-gray-darkest" />
        </div>
      )}
      <Text
        className="w-fill d-f fg-1"
        variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
      >
        <span className="ml-8 mr-8 ws-nw o-h">
          {`${t(
            'practice.on_demand_practice.editor.conversations.interaction_header',
          )} ${position}`}
        </span>
        <ClampedText lines={1} className="w-fill" key={subject}>
          {subject && ` - ${subject}`}
        </ClampedText>
      </Text>
      <div className="f-sh-0">
        {showDelete && (
          <button
            type="button"
            className="f-o-n u-resetBtn mr-8 OnDemandPracticeConversation-deleteInteraction"
            data-test-id={`deleteInteraction-${position}`}
            onClick={e => {
              e.stopPropagation();
              setShowConfirmDelete(true);
            }}
          >
            <Icon
              name="delete"
              className="fz-20 c-gray-darker h-c-gray-darkest"
            />
          </button>
        )}
        <Icon className="is-16" name={expanded ? 'caret_down' : 'caret_left'} />
      </div>
    </div>
  );
  return (
    <div ref={ref} data-handler-id={handlerId}>
      {renderHeader()}
      {expanded && (
        <div
          className={classNames(
            'Animated Animated--slideIn border-gray-light p-32 d-f fd-c',
            { 'border-bottom': hideBorder, border: !hideBorder },
          )}
        >
          <RoundedInput
            labelText={
              <>
                {t(
                  'practice.on_demand_practice.editor.conversations.interaction_subject',
                )}
                <span className="ml-4 c-danger-red">*</span>
              </>
            }
            id={`conversation-interaction-subject-${position}`}
            placeholderText={t(
              'practice.on_demand_practice.editor.conversations.interaction_subject_placeholder',
            )}
            value={subject}
            onChange={newSubject => {
              setSubject(newSubject);
              setShouldUpdate(true);
            }}
            singleLine
            hasError={!subject}
            showStrongErrors={showStrongErrors}
          />
          <hr className="w-fill mb-4" />
          {orderedResponses.map((response, orderedIndex) => {
            return (
              <Response
                key={`${id}-${response.id}`}
                response={response}
                interactionId={id}
                scenarioId={scenarioId}
                deleteResponse={deleteResponse}
                updateResponse={updateResponseApi}
                index={orderedIndex}
                setResponses={res => {
                  const updatedResponses = responses.map(r =>
                    r.id === res.id ? res : r,
                  );
                  setResponses(updatedResponses);
                  setShouldUpdate(true);
                }}
                showStrongErrors={showStrongErrors}
              />
            );
          })}
          <div className="d-f jc-sb mt-16 mb-8 ai-c">
            <button
              type="button"
              data-test-id="addResponse"
              onClick={() => addResponse()}
              className="border-0 c-brand-blue bg-white td-u haf-c-lhci ml-a"
            >
              <Icon name="add" className="mr-4" />
              {t(
                'practice.on_demand_practice.editor.conversations.add_additional_incorrect_response',
              )}
            </button>
          </div>
          {isLoading && (
            <div className="d-f fd-c">
              <div className="ml-20p">
                <Skeleton height={24} width="40%" />
              </div>
              <div className="mt-12 ta-r">
                <Skeleton height={62} width="80%" />
              </div>
              <div className="mt-12 ml-30p">
                <Skeleton height={24} width="30%" />
              </div>
              <div className="mt-12 ta-r">
                <Skeleton height={62} width="70%" />
              </div>
            </div>
          )}
        </div>
      )}
      {showConfirmDelete && (
        <Modal
          show={showConfirmDelete}
          dismissCallback={() => setShowConfirmDelete(false)}
          title={t(
            'practice.on_demand_practice.editor.conversations.delete_interaction',
          )}
          noPadding
        >
          <div className="mb-24 mt-16 fw-b pl-16">
            {t(
              'practice.on_demand_practice.editor.conversations.confirm_delete_interaction',
            )}
          </div>
          <ButtonGroup alignEnd className="mb-16 p-16">
            <Button onClick={() => setShowConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => deleteInteraction({ id, position })}
              primary
              data-test-id="confirmInteractionDelete"
            >
              {t('common.delete')}
            </Button>
          </ButtonGroup>
        </Modal>
      )}
    </div>
  );
};
export default Interaction;
